export const ANALYTICS_CONSENT_COOKIE_NAME = "ap_analytics_consent";
export const ANALYTICS_CONSENT_STORAGE_KEY = "ap_analytics_consent";
export const ANALYTICS_CONSENT_CHANGED_EVENT = "ap-analytics-consent-changed";
export const ANALYTICS_CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

export type AnalyticsConsent = "accepted" | "rejected";

export function parseAnalyticsConsent(
  raw: string | null | undefined,
): AnalyticsConsent | null {
  const value = raw?.trim();
  if (value === "accepted" || value === "rejected") {
    return value;
  }
  return null;
}

export function shouldEnablePostHog(
  consent: AnalyticsConsent | null | undefined,
): boolean {
  return consent === "accepted";
}

export function readCookieValue(
  cookieHeader: string,
  name: string,
): string | null {
  if (!cookieHeader.trim()) {
    return null;
  }

  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trim();
    const separator = trimmed.indexOf("=");
    if (separator === -1) {
      continue;
    }
    const key = trimmed.slice(0, separator).trim();
    if (key !== name) {
      continue;
    }
    try {
      return decodeURIComponent(trimmed.slice(separator + 1).trim());
    } catch {
      return trimmed.slice(separator + 1).trim();
    }
  }

  return null;
}

export function readAnalyticsConsentFromSources({
  cookieHeader,
  storageValue,
}: {
  cookieHeader?: string | null;
  storageValue?: string | null;
}): AnalyticsConsent | null {
  const fromCookie = parseAnalyticsConsent(
    readCookieValue(cookieHeader ?? "", ANALYTICS_CONSENT_COOKIE_NAME),
  );
  if (fromCookie) {
    return fromCookie;
  }
  return parseAnalyticsConsent(storageValue);
}

export function buildAnalyticsConsentCookie(consent: AnalyticsConsent): string {
  return `${ANALYTICS_CONSENT_COOKIE_NAME}=${consent}; Path=/; Max-Age=${ANALYTICS_CONSENT_MAX_AGE}; SameSite=Lax`;
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  const cookieHeader = typeof document === "undefined" ? "" : document.cookie;
  let storageValue: string | null = null;
  if (typeof localStorage !== "undefined") {
    try {
      storageValue = localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    } catch {
      storageValue = null;
    }
  }
  return readAnalyticsConsentFromSources({ cookieHeader, storageValue });
}

export function persistAnalyticsConsent(consent: AnalyticsConsent): void {
  if (typeof document !== "undefined") {
    document.cookie = buildAnalyticsConsentCookie(consent);
  }
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent);
    } catch {
      // Private mode can block storage; the cookie still persists the choice.
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ANALYTICS_CONSENT_CHANGED_EVENT, { detail: consent }),
    );
  }
}
